import React, { useMemo, useState, useContext } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TableContainer,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import { useNavigate } from "react-router-dom";

const getDaysInMonth = (monthYearStr) => {
  const [monthStr, yearStr] = monthYearStr.split(" ");
  const monthIndex = new Date(`${monthStr} 1, ${yearStr}`).getMonth();
  const year = parseInt(yearStr);
  return new Date(year, monthIndex + 1, 0).getDate();
};

export default function MonthlyDashboard({
  selectedMonth,
  activeFilter,
  vehiclesData,
  onRefresh,
}) {
  const { userInfo } = useContext(AutoTaskAuthContext) || {};
  const store_id = userInfo?.store_id;

  const [selectedCell, setSelectedCell] = useState(null); // { vehicleId, date }
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const navigate = useNavigate();
  const loggedUser = JSON.parse(localStorage.getItem("CapacitorStorage.user"));
  const user_id = loggedUser.id;
  const user_mobile = loggedUser.mobile;

  const userDataObject = {
    user_id,
    user_mobile,
    store_id,
  };

  // Generate all dates in the month
  const daysInMonth = useMemo(() => {
    const numDays = getDaysInMonth(selectedMonth);
    return Array.from({ length: numDays }, (_, i) => i + 1);
  }, [selectedMonth]);

  // Create booking map: vehicleId -> date -> bookings[]
  const bookingMap = useMemo(() => {
    const [monthStr, yearStr] = selectedMonth.split(" ");
    const monthIndex = new Date(`${monthStr} 1, ${yearStr}`).getMonth();
    const year = parseInt(yearStr);

    const map = new Map();

    vehiclesData.forEach((vehicle) => {
      const vehicleMap = new Map();

      vehicle.bookings.forEach((booking) => {
        const pickupDate = booking.trip_info?.pickup_date;
        const dropDate = booking.trip_info?.drop_date;

        if (!pickupDate) return;

        // Parse pickup date
        const [pYear, pMonth, pDay] = pickupDate.split("-").map(Number);

        // If no drop date or same as pickup, mark only pickup date
        if (!dropDate || dropDate === "" || dropDate === pickupDate) {
          if (pYear === year && pMonth - 1 === monthIndex) {
            const day = pDay;
            if (!vehicleMap.has(day)) {
              vehicleMap.set(day, []);
            }
            vehicleMap.get(day).push(booking);
          }
        } else {
          // Multi-day booking - mark all dates in range
          const [dYear, dMonth, dDay] = dropDate.split("-").map(Number);

          // Iterate through date range
          const pickupDateObj = new Date(pYear, pMonth - 1, pDay);
          const dropDateObj = new Date(dYear, dMonth - 1, dDay);

          for (
            let d = new Date(pickupDateObj);
            d <= dropDateObj;
            d.setDate(d.getDate() + 1)
          ) {
            if (d.getFullYear() === year && d.getMonth() === monthIndex) {
              const day = d.getDate();
              if (!vehicleMap.has(day)) {
                vehicleMap.set(day, []);
              }
              // Only add if not already present (avoid duplicates)
              if (
                !vehicleMap
                  .get(day)
                  .some((b) => b.booking_id === booking.booking_id)
              ) {
                vehicleMap.get(day).push(booking);
              }
            }
          }
        }
      });

      map.set(vehicle.vehicle_id, vehicleMap);
    });

    return map;
  }, [vehiclesData, selectedMonth]);

  // Create N/A vehicle types booking map: vehicleType -> date -> bookings[]
  const naVehicleTypeBookingMap = useMemo(() => {
    const [monthStr, yearStr] = selectedMonth.split(" ");
    const monthIndex = new Date(`${monthStr} 1, ${yearStr}`).getMonth();
    const year = parseInt(yearStr);

    const map = new Map();
    // Track normalized vehicle type to original casing
    const vehicleTypeMap = new Map();

    // Find the N/A vehicle entry
    const naVehicle = vehiclesData.find((v) => v.vehicle_code === "N/A");
    if (!naVehicle) return map;

    naVehicle.bookings.forEach((booking) => {
      const pickupDate = booking.trip_info?.pickup_date;
      const dropDate = booking.trip_info?.drop_date;

      if (!pickupDate) return;

      // Get vehicle type from trip_info or default to "-"
      const originalVehicleType = booking.trip_info?.vehicle_type || "-";
      const normalizedVehicleType = originalVehicleType.toLowerCase();

      // Store the first occurrence of each vehicle type (preserving original casing)
      if (!vehicleTypeMap.has(normalizedVehicleType)) {
        vehicleTypeMap.set(normalizedVehicleType, originalVehicleType);
      }

      // Use the stored original casing
      const vehicleType = vehicleTypeMap.get(normalizedVehicleType);

      if (!map.has(vehicleType)) {
        map.set(vehicleType, new Map());
      }

      const vehicleTypeBookingMap = map.get(vehicleType);

      // Parse pickup date
      const [pYear, pMonth, pDay] = pickupDate.split("-").map(Number);

      // If no drop date or same as pickup, mark only pickup date
      if (!dropDate || dropDate === "" || dropDate === pickupDate) {
        if (pYear === year && pMonth - 1 === monthIndex) {
          const day = pDay;
          if (!vehicleTypeBookingMap.has(day)) {
            vehicleTypeBookingMap.set(day, []);
          }
          vehicleTypeBookingMap.get(day).push(booking);
        }
      } else {
        // Multi-day booking - mark all dates in range
        const [dYear, dMonth, dDay] = dropDate.split("-").map(Number);

        // Iterate through date range
        const pickupDateObj = new Date(pYear, pMonth - 1, pDay);
        const dropDateObj = new Date(dYear, dMonth - 1, dDay);

        for (
          let d = new Date(pickupDateObj);
          d <= dropDateObj;
          d.setDate(d.getDate() + 1)
        ) {
          if (d.getFullYear() === year && d.getMonth() === monthIndex) {
            const day = d.getDate();
            if (!vehicleTypeBookingMap.has(day)) {
              vehicleTypeBookingMap.set(day, []);
            }
            // Only add if not already present (avoid duplicates)
            if (
              !vehicleTypeBookingMap
                .get(day)
                .some((b) => b.booking_id === booking.booking_id)
            ) {
              vehicleTypeBookingMap.get(day).push(booking);
            }
          }
        }
      }
    });

    return map;
  }, [vehiclesData, selectedMonth]);

  // Get unique vehicle types from N/A bookings
  const naVehicleTypes = useMemo(() => {
    return Array.from(naVehicleTypeBookingMap.keys()).sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase()),
    );
  }, [naVehicleTypeBookingMap]);

  // Get all unique vehicles (ALWAYS show all vehicles, not just those with bookings)
  const allVehicles = useMemo(() => {
    // Create a map to deduplicate vehicles by vehicle_id
    const vehicleMap = new Map();

    vehiclesData.forEach((vehicle) => {
      if (!vehicleMap.has(vehicle.vehicle_id)) {
        vehicleMap.set(vehicle.vehicle_id, {
          vehicle_id: vehicle.vehicle_id,
          vehicle_name: vehicle.vehicle_name,
          vehicle_number: vehicle.vehicle_number,
          vehicle_code: vehicle.vehicle_code,
          vehicle_type: vehicle.vehicle_type || "-",
          is_temp: vehicle.is_temp,
        });
      }
    });

    // Keep the order the API sent (VehicleMaster sl_no, then temp vehicles,
    // then "Not Assigned" last) so rows line up with the Day view.
    return Array.from(vehicleMap.values());
  }, [vehiclesData]);

  const handleCellClick = (
    vehicle,
    date,
    isVehicleType = false,
    vehicleType = null,
  ) => {
    if (isVehicleType) {
      // Handle N/A vehicle type cell click
      const vehicleTypeBookings = naVehicleTypeBookingMap.get(vehicleType);
      const bookings = vehicleTypeBookings?.get(date) || [];

      setSelectedCell({
        vehicle: {
          vehicle_code: "N/A",
          vehicle_name: `${vehicleType} (Unassigned)`,
          vehicle_number: "N/A",
          vehicle_id: `na-${vehicleType}`,
        },
        date,
        bookings,
        isVehicleType: true,
        vehicleType,
      });

      if (bookings.length === 1) {
        setSelectedBooking(bookings[0]);
      } else {
        setSelectedBooking(null);
      }
    } else {
      const vehicleBookings = bookingMap.get(vehicle.vehicle_id);
      const bookings = vehicleBookings?.get(date) || [];

      setSelectedCell({ vehicle, date, bookings, isVehicleType: false });
      if (bookings.length === 1) {
        setSelectedBooking(bookings[0]);
      } else {
        setSelectedBooking(null);
      }
    }
  };

  const handleBookingSelect = (booking) => {
    setSelectedBooking(booking);
  };

  const handleDeleteClick = (e, booking) => {
    e.stopPropagation();
    setBookingToDelete(booking);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!bookingToDelete) return;

    setDeleting(true);
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/travel/booking/${bookingToDelete.booking_id}`,
      );

      if (response.data.success) {
        onRefresh();
        setDeleteDialogOpen(false);
        setBookingToDelete(null);
        setSelectedCell(null);
        setSelectedBooking(null);
      } else {
        alert(response.data.statusMessage || "Failed to delete booking");
      }
    } catch (err) {
      alert("Network error. Failed to delete booking.");
      console.error("Delete error:", err);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setBookingToDelete(null);
  };

  const handleEditBtn = () => {
    navigate("/travel/booking", {
      state: { booking_id: selectedBooking.booking_id },
    });
  };

  const handleCreateBooking = () => {
    navigate("/travel/booking", {
      state: {
        prefilledVehicle: {
          vehicle_id: selectedCell.vehicle.vehicle_id,
          vehicle_name: selectedCell.vehicle.vehicle_name,
          vehicle_number: selectedCell.vehicle.vehicle_number,
          vehicle_code: selectedCell.vehicle.vehicle_code,
        },
      },
    });
  };

  const formatTime = (time) => {
    if (!time) return "N/A";
    const [hours, minutes] = time.split(":");
    const hr = parseInt(hours);
    const ampm = hr >= 12 ? "PM" : "AM";
    const displayHr = hr % 12 || 12;
    return `${displayHr}:${minutes} ${ampm}`;
  };

  // Get cell display info
  const getCellInfo = (vehicleId, date) => {
    const vehicleBookings = bookingMap.get(vehicleId);
    const bookings = vehicleBookings?.get(date) || [];

    if (bookings.length === 0) {
      return { status: "free", count: 0, bookings: [] };
    }

    return {
      status: "booked",
      count: bookings.length,
      bookings: bookings,
    };
  };

  // Get cell display info for N/A vehicle types
  const getVehicleTypeCellInfo = (vehicleType, date) => {
    const vehicleTypeBookings = naVehicleTypeBookingMap.get(vehicleType);
    const bookings = vehicleTypeBookings?.get(date) || [];

    if (bookings.length === 0) {
      return { status: "free", count: 0, bookings: [] };
    }

    return {
      status: "booked",
      count: bookings.length,
      bookings: bookings,
    };
  };

  return (
    <Box sx={{ display: "flex", height: "100%", overflow: "hidden" }}>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          p: { xs: 0.8, sm: 1, md: 1.2 },
          overflow: "hidden",
          minWidth: 0,
        }}
      >
        <Typography
          variant="h6"
          fontWeight={600}
          sx={{ mb: 1, fontSize: { xs: "0.9rem", md: "1rem" } }}
        >
          {selectedMonth} - Monthly View
        </Typography>

        <TableContainer
          component={Paper}
          sx={{ flex: 1, overflow: "auto", position: "relative" }}
        >
          <Table size="small" stickyHeader sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "0.6rem", md: "0.65rem" },
                    py: 0.4,
                    px: 0.8,
                    position: "sticky",
                    left: 0,
                    bgcolor: "background.paper",
                    zIndex: 3,
                    minWidth: 70,
                    width: 70,
                    maxWidth: 70,
                  }}
                >
                  Vehicle
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "0.6rem", md: "0.65rem" },
                    py: 0.4,
                    px: 0.8,
                    position: "sticky",
                    left: 70,
                    bgcolor: "background.paper",
                    zIndex: 3,
                    minWidth: 70,
                    width: 70,
                    maxWidth: 70,
                  }}
                >
                  Type
                </TableCell>
                {daysInMonth.map((day) => (
                  <TableCell
                    key={day}
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.6rem", md: "0.65rem" },
                      py: 0.4,
                      px: 0.3,
                      minWidth: 42,
                      width: 42,
                      maxWidth: 42,
                    }}
                  >
                    {day}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {allVehicles.map((vehicle) => (
                <React.Fragment key={vehicle.vehicle_id}>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontSize: { xs: "0.6rem", md: 13.5 },
                        py: 1,
                        px: 0.8,
                        position: "sticky",
                        left: 0,
                        bgcolor: "background.paper",
                        zIndex: 1,
                        fontWeight: 500,
                        minWidth: 70,
                        width: 70,
                        maxWidth: 70,
                        color: "#505050",
                      }}
                    >
                      {vehicle.vehicle_code}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: { xs: "0.6rem", md: 13.5 },
                        py: 1,
                        px: 0.8,
                        position: "sticky",
                        left: 70,
                        bgcolor: "background.paper",
                        zIndex: 1,
                        fontWeight: 400,
                        minWidth: 70,
                        width: 70,
                        maxWidth: 70,
                        color: "#707070",
                      }}
                    >
                      {vehicle.vehicle_code === "N/A"
                        ? "-"
                        : vehicle.vehicle_type}
                    </TableCell>
                    {daysInMonth.map((day) => {
                      const cellInfo = getCellInfo(vehicle.vehicle_id, day);
                      const isSelected =
                        !selectedCell?.isVehicleType &&
                        selectedCell?.vehicle?.vehicle_id ===
                          vehicle.vehicle_id &&
                        selectedCell?.date === day;

                      return (
                        <TableCell
                          key={day}
                          align="center"
                          onClick={() => handleCellClick(vehicle, day)}
                          sx={{
                            py: 0.3,
                            px: 0.3,
                            cursor: "pointer",
                            color: isSelected ? "#fff" : "#00295e",
                            bgcolor: isSelected
                              ? "#5ba3d6" // light blue (selected)
                              : cellInfo.status === "booked"
                              ? "#c7e6ff" // light green (booked)
                              : "inherit",
                            "&:hover": {
                              color: "#ffff",
                              bgcolor: isSelected
                                ? "#BBDEFB" // darker blue on hover
                                : cellInfo.status === "booked"
                                ? "#6593b9" // darker green on hover
                                : "#F5F5F5", // light grey hover
                            },
                            borderRight: "1px solid #E0E0E0",
                            minWidth: 42,
                            width: 42,
                            maxWidth: 42,
                          }}
                        >
                          {cellInfo.status === "booked" ? (
                            <Tooltip
                              title={`${cellInfo.count} booking${
                                cellInfo.count > 1 ? "s" : ""
                              }`}
                              arrow
                            >
                              <Typography sx={{ fontSize: 14 }}>
                                {cellInfo.count}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography
                              sx={{
                                fontSize: "0.65rem",
                                color: "text.disabled",
                              }}
                            >
                              -
                            </Typography>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>

                  {/* N/A Vehicle Type Breakdown */}
                  {vehicle.vehicle_code === "N/A" &&
                    naVehicleTypes.length > 0 && (
                      <>
                        {naVehicleTypes.map((vehicleType) => (
                          <TableRow
                            key={`${vehicle.vehicle_id}-${vehicleType}`}
                          >
                            <TableCell
                              sx={{
                                fontSize: { xs: "0.6rem", md: 12 },
                                py: 0.8,
                                px: 0.8,
                                pl: 2.5,
                                position: "sticky",
                                left: 0,
                                bgcolor: "background.paper",
                                zIndex: 1,
                                fontWeight: 400,
                                minWidth: 70,
                                width: 70,
                                maxWidth: 70,
                                color: "#707070",
                                fontStyle: "italic",
                              }}
                            >
                              ↳ N/A
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: { xs: "0.6rem", md: 12 },
                                py: 0.8,
                                px: 0.8,
                                position: "sticky",
                                left: 70,
                                bgcolor: "background.paper",
                                zIndex: 1,
                                fontWeight: 400,
                                minWidth: 70,
                                width: 70,
                                maxWidth: 70,
                                color: "#707070",
                                fontStyle: "italic",
                              }}
                            >
                              {vehicleType}
                            </TableCell>
                            {daysInMonth.map((day) => {
                              const cellInfo = getVehicleTypeCellInfo(
                                vehicleType,
                                day,
                              );
                              const isSelected =
                                selectedCell?.isVehicleType &&
                                selectedCell?.vehicleType === vehicleType &&
                                selectedCell?.date === day;

                              return (
                                <TableCell
                                  key={day}
                                  align="center"
                                  onClick={() =>
                                    handleCellClick(
                                      vehicle,
                                      day,
                                      true,
                                      vehicleType,
                                    )
                                  }
                                  sx={{
                                    py: 0.3,
                                    px: 0.3,
                                    cursor: "pointer",
                                    color: isSelected ? "#fff" : "#00295e",
                                    bgcolor: isSelected
                                      ? "#5ba3d6"
                                      : cellInfo.status === "booked"
                                      ? "#ffe4b3" // different shade for vehicle types
                                      : "inherit",
                                    "&:hover": {
                                      color: "#ffff",
                                      bgcolor: isSelected
                                        ? "#BBDEFB"
                                        : cellInfo.status === "booked"
                                        ? "#d4a574"
                                        : "#F5F5F5",
                                    },
                                    borderRight: "1px solid #E0E0E0",
                                    minWidth: 42,
                                    width: 42,
                                    maxWidth: 42,
                                  }}
                                >
                                  {cellInfo.status === "booked" ? (
                                    <Tooltip
                                      title={`${cellInfo.count} booking${
                                        cellInfo.count > 1 ? "s" : ""
                                      }`}
                                      arrow
                                    >
                                      <Typography sx={{ fontSize: 13 }}>
                                        {cellInfo.count}
                                      </Typography>
                                    </Tooltip>
                                  ) : (
                                    <Typography
                                      sx={{
                                        fontSize: "0.65rem",
                                        color: "text.disabled",
                                      }}
                                    >
                                      -
                                    </Typography>
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </>
                    )}
                </React.Fragment>
              ))}
              {allVehicles.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={daysInMonth.length + 2}
                    align="center"
                    sx={{ py: 3 }}
                  >
                    <Typography
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.75rem", md: "0.85rem" } }}
                    >
                      No vehicles found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Right Panel - Booking Details */}
      <Box
        sx={{
          width: { xs: 240, md: 280 },
          borderLeft: 1,
          borderColor: "divider",
          p: { xs: 0.8, md: 1.2 },
          overflow: "auto",
          bgcolor: "grey.50",
          display: { xs: selectedCell ? "block" : "none", md: "block" },
        }}
      >
        {selectedCell ? (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{ fontSize: { xs: "0.85rem", md: "0.95rem" } }}
              >
                {selectedCell.bookings.length > 0
                  ? selectedCell.bookings.length > 1
                    ? `${selectedCell.bookings.length} Bookings`
                    : "Booking Details"
                  : "Vehicle Details"}
              </Typography>
              <IconButton
                onClick={() => {
                  setSelectedCell(null);
                  setSelectedBooking(null);
                }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Vehicle Info Card */}
            <Card variant="outlined" sx={{ mb: 0.6 }}>
              <CardContent sx={{ p: 0.8, "&:last-child": { pb: 0.8 } }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mb={0.2}
                  sx={{ fontSize: { xs: "0.6rem", md: "0.65rem" } }}
                >
                  {selectedCell.isVehicleType ? "Vehicle Type" : "Vehicle"}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                >
                  {selectedCell.vehicle.vehicle_code} -{" "}
                  {selectedCell.vehicle.vehicle_name}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: { xs: "0.6rem", md: "0.65rem" } }}
                >
                  {selectedCell.vehicle.vehicle_number}
                </Typography>
              </CardContent>
            </Card>

            {/* Date Card */}
            <Card variant="outlined" sx={{ mb: 0.6 }}>
              <CardContent sx={{ p: 0.8, "&:last-child": { pb: 0.8 } }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mb={0.2}
                  sx={{ fontSize: { xs: "0.6rem", md: "0.65rem" } }}
                >
                  Date
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                >
                  {selectedCell.date} {selectedMonth}
                </Typography>
              </CardContent>
            </Card>

            {selectedCell.bookings.length > 0 ? (
              selectedCell.bookings.length > 1 ? (
                <>
                  {/* Multiple Bookings - Show list */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mb={1}
                    sx={{ fontSize: { xs: "0.6rem", md: "0.65rem" } }}
                  >
                    Select a booking to view details:
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.8,
                      mb: 1,
                    }}
                  >
                    {selectedCell.bookings.map((booking) => (
                      <Card
                        key={booking.booking_id}
                        variant="outlined"
                        onClick={() => handleBookingSelect(booking)}
                        sx={{
                          cursor: "pointer",
                          bgcolor:
                            selectedBooking?.booking_id === booking.booking_id
                              ? "action.selected"
                              : "inherit",
                          "&:hover": {
                            bgcolor: "action.hover",
                          },
                        }}
                      >
                        <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                          <Typography
                            sx={{
                              fontSize: { xs: "0.7rem", md: "0.75rem" },
                              fontWeight: 500,
                            }}
                          >
                            {formatTime(booking.trip_time)} -{" "}
                            {booking.customer_name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: { xs: "0.6rem", md: "0.65rem" },
                              color: "text.secondary",
                            }}
                          >
                            {booking.booking_id} • ₹{booking.amount}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>

                  {/* Show selected booking details */}
                  {selectedBooking && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.8,
                      }}
                    >
                      <Card variant="outlined">
                        <CardContent
                          sx={{ p: 0.8, "&:last-child": { pb: 0.8 } }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={0.3}
                            sx={{ fontSize: { xs: "0.6rem", md: "0.65rem" } }}
                          >
                            Customer
                          </Typography>
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{ fontSize: { xs: "0.85rem", md: "0.9rem" } }}
                          >
                            {selectedBooking.customer_name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                          >
                            📱 {selectedBooking.customer_mobile}
                          </Typography>
                        </CardContent>
                      </Card>

                      <Card variant="outlined">
                        <CardContent
                          sx={{ p: 0.8, "&:last-child": { pb: 0.8 } }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ fontSize: { xs: "0.6rem", md: "0.65rem" } }}
                          >
                            Driver
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                          >
                            {selectedBooking.driver_name}
                          </Typography>
                          {selectedBooking.driver_phone && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: { xs: "0.6rem", md: "0.65rem" } }}
                            >
                              {selectedBooking.driver_phone}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>

                      <Card variant="outlined">
                        <CardContent
                          sx={{ p: 0.8, "&:last-child": { pb: 0.8 } }}
                        >
                          <Grid container spacing={1.5}>
                            <Grid item xs={6}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{
                                  fontSize: { xs: "0.6rem", md: "0.65rem" },
                                }}
                              >
                                Amount
                              </Typography>
                              <Typography
                                variant="h5"
                                fontWeight={600}
                                color="primary.main"
                                sx={{ fontSize: { xs: "1rem", md: "1.15rem" } }}
                              >
                                ₹{selectedBooking.amount}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                mb={0.5}
                                sx={{
                                  fontSize: { xs: "0.6rem", md: "0.65rem" },
                                }}
                              >
                                Status
                              </Typography>
                              <Chip
                                label={selectedBooking.type}
                                size="small"
                                color={
                                  selectedBooking.is_pending
                                    ? "warning"
                                    : "success"
                                }
                                sx={{
                                  fontSize: { xs: "0.6rem", md: "0.65rem" },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>

                      {selectedBooking.trip_details && (
                        <Card variant="outlined">
                          <CardContent
                            sx={{ p: 0.8, "&:last-child": { pb: 0.8 } }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              mb={0.8}
                              sx={{ fontSize: { xs: "0.6rem", md: "0.65rem" } }}
                            >
                              Trip Details
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                            >
                              {selectedBooking.trip_details}
                            </Typography>
                          </CardContent>
                        </Card>
                      )}

                      <Button
                        onClick={handleEditBtn}
                        variant="contained"
                        startIcon={<EditIcon />}
                        fullWidth
                      >
                        Edit
                      </Button>
                      <Button
                        onClick={(e) => handleDeleteClick(e, selectedBooking)}
                        variant="outlined"
                        color="error"
                        fullWidth
                      >
                        Cancel
                      </Button>
                    </Box>
                  )}
                </>
              ) : (
                /* Single Booking */
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}
                >
                  <Card variant="outlined">
                    <CardContent sx={{ p: 0.8, "&:last-child": { pb: 0.8 } }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mb={0.3}
                        sx={{ fontSize: { xs: "0.6rem", md: "0.65rem" } }}
                      >
                        Booking ID
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{ fontSize: { xs: "0.8rem", md: "0.85rem" } }}
                      >
                        {selectedCell.bookings[0].booking_id}
                      </Typography>
                    </CardContent>
                  </Card>

                  <Card variant="outlined">
                    <CardContent sx={{ p: 0.8, "&:last-child": { pb: 0.8 } }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mb={0.3}
                        sx={{ fontSize: { xs: "0.6rem", md: "0.65rem" } }}
                      >
                        Customer
                      </Typography>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ fontSize: { xs: "0.85rem", md: "0.9rem" } }}
                      >
                        {selectedCell.bookings[0].customer_name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                      >
                        📱 {selectedCell.bookings[0].customer_mobile}
                      </Typography>
                    </CardContent>
                  </Card>

                  <Card variant="outlined">
                    <CardContent sx={{ p: 0.8, "&:last-child": { pb: 0.8 } }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ fontSize: { xs: "0.6rem", md: "0.65rem" } }}
                      >
                        Driver
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                      >
                        {selectedCell.bookings[0].driver_name}
                      </Typography>
                      {selectedCell.bookings[0].driver_phone && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: { xs: "0.6rem", md: "0.65rem" } }}
                        >
                          {selectedCell.bookings[0].driver_phone}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>

                  <Card variant="outlined">
                    <CardContent sx={{ p: 0.8, "&:last-child": { pb: 0.8 } }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mb={0.8}
                        sx={{ fontSize: { xs: "0.6rem", md: "0.65rem" } }}
                      >
                        Trip Info
                      </Typography>
                      <Box sx={{ mb: 0.6 }}>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                        >
                          ⏰ {formatTime(selectedCell.bookings[0].trip_time)}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                        >
                          Mode:{" "}
                          {selectedCell.bookings[0].trip_info?.mode || "N/A"}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                        >
                          Passengers:{" "}
                          {selectedCell.bookings[0].trip_info?.pax || "N/A"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: { xs: "0.6rem", md: "0.65rem" } }}
                        >
                          Pickup:{" "}
                          {selectedCell.bookings[0].trip_info?.pickup_date}
                        </Typography>
                        <br />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: { xs: "0.6rem", md: "0.65rem" } }}
                        >
                          Drop: {selectedCell.bookings[0].trip_info?.drop_date}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>

                  <Card variant="outlined">
                    <CardContent sx={{ p: 0.8, "&:last-child": { pb: 0.8 } }}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={6}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ fontSize: { xs: "0.6rem", md: "0.65rem" } }}
                          >
                            Amount
                          </Typography>
                          <Typography
                            variant="h5"
                            fontWeight={600}
                            color="primary.main"
                            sx={{ fontSize: { xs: "1rem", md: "1.15rem" } }}
                          >
                            ₹{selectedCell.bookings[0].amount}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={0.5}
                            sx={{ fontSize: { xs: "0.6rem", md: "0.65rem" } }}
                          >
                            Status
                          </Typography>
                          <Chip
                            label={selectedCell.bookings[0].type}
                            size="small"
                            color={
                              selectedCell.bookings[0].is_pending
                                ? "warning"
                                : "success"
                            }
                            sx={{ fontSize: { xs: "0.6rem", md: "0.65rem" } }}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {selectedCell.bookings[0].trip_details && (
                    <Card variant="outlined">
                      <CardContent sx={{ p: 0.8, "&:last-child": { pb: 0.8 } }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={0.8}
                          sx={{ fontSize: { xs: "0.6rem", md: "0.65rem" } }}
                        >
                          Trip Details
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                        >
                          {selectedCell.bookings[0].trip_details}
                        </Typography>
                      </CardContent>
                    </Card>
                  )}

                  {selectedCell.bookings[0].instructions && (
                    <Card variant="outlined">
                      <CardContent sx={{ p: 0.8, "&:last-child": { pb: 0.8 } }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={0.8}
                          sx={{ fontSize: { xs: "0.6rem", md: "0.65rem" } }}
                        >
                          Instructions
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                        >
                          {selectedCell.bookings[0].instructions}
                        </Typography>
                      </CardContent>
                    </Card>
                  )}

                  <Button
                    onClick={() => {
                      navigate("/travel/booking", {
                        state: {
                          booking_id: selectedCell.bookings[0].booking_id,
                        },
                      });
                    }}
                    variant="contained"
                    startIcon={<EditIcon />}
                    fullWidth
                  >
                    Edit
                  </Button>
                  <Button
                    onClick={(e) =>
                      handleDeleteClick(e, selectedCell.bookings[0])
                    }
                    variant="outlined"
                    color="error"
                    fullWidth
                  >
                    Cancel
                  </Button>
                </Box>
              )
            ) : (
              /* No bookings - Show create button */
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
                <Card variant="outlined">
                  <CardContent sx={{ p: 0.8, "&:last-child": { pb: 0.8 } }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
                    >
                      {selectedCell.isVehicleType
                        ? "No bookings for this vehicle type on this date."
                        : "This vehicle is available on this date."}
                    </Typography>
                  </CardContent>
                </Card>

                {!selectedCell.isVehicleType && (
                  <Button
                    onClick={handleCreateBooking}
                    variant="contained"
                    startIcon={<AddIcon />}
                    fullWidth
                  >
                    Create Booking
                  </Button>
                )}
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              textAlign: "center",
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: { xs: "0.75rem", md: "0.85rem" } }}
            >
              Click on a cell to view details
            </Typography>
          </Box>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this booking?
            {bookingToDelete && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: "grey.100", borderRadius: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  Booking ID: {bookingToDelete.booking_id}
                </Typography>
                <Typography variant="body2">
                  Customer: {bookingToDelete.customer_name}
                </Typography>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={
              deleting ? <CircularProgress size={16} /> : <DeleteIcon />
            }
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
